import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Star, Users, Wrench, Clock, Trash2, Plus, 
  Calendar, CheckCircle, XCircle, Search,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const AdminPromotionsPage = () => {
  const { api: getApi } = useAuth();
  const api = getApi();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sellers'); // 'sellers' or 'tradies'
  const [promotions, setPromotions] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [tradies, setTradies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('7');
  const [promotingUser, setPromotingUser] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [promoRes, usersRes] = await Promise.all([
        api.get('/admin/promotions'),
        api.get('/admin/promotable-users')
      ]);
      setPromotions(promoRes.data.promotions || []);
      setSellers(usersRes.data.sellers || []);
      setTradies(usersRes.data.tradies || []);
    } catch (err) {
      console.error('Failed to fetch data', err);
      toast.error('Failed to load promotion data');
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async (userId, type) => {
    try {
      setPromotingUser(userId);
      await api.post('/admin/promotions', {
        user_id: userId,
        promotion_type: type,
        duration_days: parseInt(selectedDuration)
      });
      toast.success(`User promoted for ${selectedDuration} days!`);
      fetchData();
    } catch (err) {
      console.error('Failed to promote user', err);
      toast.error(err.response?.data?.detail || 'Failed to promote user');
    } finally {
      setPromotingUser(null);
    }
  };

  const handleRemovePromotion = async (promotionId, userName) => {
    if (!window.confirm(`Remove promotion for ${userName}?`)) return;
    
    try {
      await api.delete(`/admin/promotions/${promotionId}`);
      toast.success('Promotion removed');
      fetchData();
    } catch (err) {
      console.error('Failed to remove promotion', err);
      toast.error('Failed to remove promotion');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const filteredUsers = (activeTab === 'sellers' ? sellers : tradies).filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activePromotions = promotions.filter(p => p.is_active && p.promotion_type === activeTab.slice(0, -1));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            <Star className="inline h-8 w-8 text-amber-500 mr-2" />
            Featured Carousel
          </h1>
          <p className="text-stone-500 mt-2">
            Promote sellers and tradies to appear in the homepage carousel
          </p>
        </div>

        {/* Active Promotions Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Users className="h-5 w-5 text-teal-600" />
              </div>
              <h3 className="font-semibold text-stone-900">Featured Sellers</h3>
            </div>
            <p className="text-3xl font-bold text-teal-600">
              {promotions.filter(p => p.is_active && p.promotion_type === 'seller').length}
            </p>
            <p className="text-sm text-stone-500">currently in carousel</p>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Wrench className="h-5 w-5 text-blue-700" />
              </div>
              <h3 className="font-semibold text-stone-900">Featured Tradies</h3>
            </div>
            <p className="text-3xl font-bold text-blue-700">
              {promotions.filter(p => p.is_active && p.promotion_type === 'tradie').length}
            </p>
            <p className="text-sm text-stone-500">currently in carousel</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'sellers' ? 'default' : 'outline'}
            onClick={() => setActiveTab('sellers')}
            className={activeTab === 'sellers' ? 'bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600' : ''}
          >
            <Users className="h-4 w-4 mr-2" />
            Sellers ({sellers.length})
          </Button>
          <Button
            variant={activeTab === 'tradies' ? 'default' : 'outline'}
            onClick={() => setActiveTab('tradies')}
            className={activeTab === 'tradies' ? 'bg-blue-700 hover:bg-blue-800' : ''}
          >
            <Wrench className="h-4 w-4 mr-2" />
            Tradies ({tradies.length})
          </Button>
        </div>

        {/* Currently Promoted Section */}
        {activePromotions.length > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200 p-6 mb-6">
            <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              Currently Featured {activeTab === 'sellers' ? 'Sellers' : 'Tradies'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activePromotions.map(promo => (
                <div key={promo.id} className="bg-white rounded-lg border border-amber-200 p-4 flex items-center gap-3">
                  {promo.user_photo ? (
                    <img src={promo.user_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center">
                      <Users className="h-6 w-6 text-stone-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900 truncate">{promo.user_name}</p>
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {getDaysRemaining(promo.ends_at)} days left
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemovePromotion(promo.id, promo.user_name)}
                    className="text-teal-600 hover:text-teal-600 hover:bg-teal-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search and Duration */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              placeholder={`Search ${activeTab}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-stone-600">Duration:</span>
            <Select value={selectedDuration} onValueChange={setSelectedDuration}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 day</SelectItem>
                <SelectItem value="3">3 days</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="21">21 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">User</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Location</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Promotion</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-stone-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {user.profile_photo ? (
                          <img src={user.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                            {activeTab === 'sellers' ? (
                              <Users className="h-5 w-5 text-stone-400" />
                            ) : (
                              <Wrench className="h-5 w-5 text-stone-400" />
                            )}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-stone-900">{user.name}</p>
                          <p className="text-xs text-stone-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600">
                      {user.location || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        user.subscription_status === 'active' || user.subscription_status === 'free_for_life'
                          ? 'bg-green-100 text-green-700'
                          : user.subscription_status === 'trial'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-stone-100 text-stone-600'
                      }`}>
                        {user.subscription_status || 'free'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {user.is_promoted ? (
                        <span className="inline-flex items-center gap-1 text-amber-600 text-sm">
                          <Star className="h-4 w-4 fill-amber-500" />
                          Until {formatDate(user.promotion_ends)}
                        </span>
                      ) : (
                        <span className="text-stone-400 text-sm">Not featured</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {user.is_promoted ? (
                        <span className="text-xs text-stone-400">Already promoted</span>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handlePromote(user.id, activeTab === 'sellers' ? 'seller' : 'tradie')}
                          disabled={promotingUser === user.id}
                          className={activeTab === 'sellers' ? 'bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600' : 'bg-blue-700 hover:bg-blue-800'}
                        >
                          {promotingUser === user.id ? (
                            'Promoting...'
                          ) : (
                            <>
                              <Plus className="h-4 w-4 mr-1" />
                              Promote
                            </>
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-stone-500">
                      No {activeTab} found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPromotionsPage;
